import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {PullRequestErrorState} from '../components/PullRequestErrorState'
import {DiffComparisonViewer} from '../components/DiffComparisonViewer'
import {Suspense, useEffect} from 'react'
import {pullRequestsFilesRoute, pullRequestsFilesWithRangeRoute} from './files-route'
import {useRouteQuery} from '@github-ui/react-core/future/use-route-query'
import {pullRequestsLayoutRoute} from './layout-route'
import type {PullRequest} from '../types'
import type {FilesRoutePayload} from './route-payload-types'
import {AnalyticsProvider} from '@github-ui/analytics-provider'
import {usePullRequestAnalytics} from '../hooks/use-pull-request-analytics'

export function FilesEntrypointFuture() {
  return (
    <ErrorBoundary critical fallback={<PullRequestErrorState text="Changes cannot be loaded" />}>
      <Files />
    </ErrorBoundary>
  )
}

export function FilesWithRangeEntrypointFuture() {
  return (
    <ErrorBoundary critical fallback={<PullRequestErrorState text="Changes cannot be loaded" />}>
      <FilesWithRange />
    </ErrorBoundary>
  )
}

function Files() {
  const {
    data: {pullRequest},
  } = useRouteQuery(pullRequestsLayoutRoute, 'mainQuery')
  const {data: routeData} = useRouteQuery(pullRequestsFilesRoute, 'mainQuery')

  return (
    <Suspense>
      <FilesComponent pullRequest={pullRequest} routeData={routeData} />
    </Suspense>
  )
}

function FilesWithRange() {
  const {
    data: {pullRequest},
  } = useRouteQuery(pullRequestsLayoutRoute, 'mainQuery')
  const {data: routeData} = useRouteQuery(pullRequestsFilesWithRangeRoute, 'mainQuery')

  return (
    <Suspense>
      <FilesComponent pullRequest={pullRequest} routeData={routeData} />
    </Suspense>
  )
}

function FilesAnalytics() {
  const {sendPullRequestAnalyticsEvent} = usePullRequestAnalytics()

  useEffect(() => {
    sendPullRequestAnalyticsEvent('page_view.files_page', 'PAGE_VIEW', {
      browser_width: typeof window !== 'undefined' ? window.innerWidth : 0,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return null
}

export function FilesComponent({pullRequest, routeData}: {pullRequest: PullRequest; routeData: FilesRoutePayload}) {
  // Use a key that changes when the data changes to force DiffComparisonViewer to remount
  // This ensures that when routeData updates (e.g., after soft navigation), the viewer
  // gets the fresh data instead of using stale initialData
  const viewerKey = `${routeData.pullRequest.comparison.baseOid}-${routeData.pullRequest.comparison.headOid}`

  return (
    <AnalyticsProvider
      appName="pull-request-files"
      category="files_tab"
      metadata={{
        pull_request_id: pullRequest.id.toString(),
        base_sha: routeData.pullRequest.comparison.baseOid,
        head_sha: routeData.pullRequest.comparison.headOid,
      }}
    >
      <FilesAnalytics />
      <Suspense>
        <DiffComparisonViewer.Viewer
          key={viewerKey}
          basePath={pullRequest.pathName}
          initialData={routeData}
          useAdvancedToolbar
          useChangeGroups
          useInlineComments
          useEditOptions
          useFileTree
        />
      </Suspense>
    </AnalyticsProvider>
  )
}

try{ FilesEntrypointFuture.displayName ||= 'FilesEntrypointFuture' } catch {}
try{ FilesWithRangeEntrypointFuture.displayName ||= 'FilesWithRangeEntrypointFuture' } catch {}
try{ Files.displayName ||= 'Files' } catch {}
try{ FilesWithRange.displayName ||= 'FilesWithRange' } catch {}
try{ FilesAnalytics.displayName ||= 'FilesAnalytics' } catch {}
try{ FilesComponent.displayName ||= 'FilesComponent' } catch {}